import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../services/api';

/** requires_alcohol: 1 = アルコール測定が必要な業務（勤怠の追加・修正画面の案内に使う） */
export interface WorkTypeMaster { name: string; emoji: string; requires_alcohol?: number }

/** 既定の業務種別（マスタ未設定・取得失敗時のフォールバック。打刻画面と同じ絵文字） */
export const DEFAULT_WORK_TYPES: WorkTypeMaster[] = [
  { name: '航海',      emoji: '🚢' },
  { name: '荷役',      emoji: '📦' },
  { name: '当直',      emoji: '👁' },
  { name: '点検・整備', emoji: '🔧' },
  { name: '係船作業',  emoji: '⚓' },
  { name: '清掃',      emoji: '🧹' },
  { name: '訓練',      emoji: '📋' },
  { name: 'その他',    emoji: '📝' },
];

export const DEFAULT_WORK_TYPE_OPTIONS = DEFAULT_WORK_TYPES.map(t => t.name);

/** 船舶の業務種別マスタ（名称＋絵文字）。オフライン時はキャッシュ、無ければ既定8種 */
export function useVesselWorkTypes(vesselId?: string): WorkTypeMaster[] {
  const [types, setTypes] = useState<WorkTypeMaster[]>(DEFAULT_WORK_TYPES);
  useEffect(() => {
    let alive = true;
    const vid = vesselId || localStorage.getItem('catms_last_vessel_id') || localStorage.getItem('catms_device_vessel_id') || '';
    if (!vid) return;
    // 同じ名前が複数登録されていても、選択肢としては1つだけ見せる
    type Row = { name?: string; emoji?: string; requires_alcohol?: number };
    const toTypes = (rows: Row[]): WorkTypeMaster[] => {
      const seen = new Set<string>();
      const out: WorkTypeMaster[] = [];
      for (const t of rows) {
        const name = String(t?.name ?? '').trim();
        if (!name) continue;
        const key = name.replace(/\s+/g, '');
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ name, emoji: String(t?.emoji || '📋'), requires_alcohol: Number(t?.requires_alcohol ?? 0) });
      }
      return out;
    };
    const fromCache = () => {
      try {
        const cached = JSON.parse(localStorage.getItem(`catms_work_types_${vid}`) ?? '[]') as Row[];
        const list = toTypes(cached);
        if (alive && list.length) setTypes(list);
      } catch { /* ignore */ }
    };
    apiFetch<{ data?: Row[] }>(`/vessels/${vid}/work-types`).then(res => {
      const list = toTypes(res?.data ?? []);
      if (!alive) return;
      if (list.length) setTypes(list); else fromCache();
    }).catch(fromCache);
    return () => { alive = false; };
  }, [vesselId]);
  return types;
}

/** 業務種別マスタを選択肢（名称の配列）にする */
export function useVesselWorkTypeOptions(vesselId?: string): string[] {
  const types = useVesselWorkTypes(vesselId);
  return useMemo(() => types.map(t => t.name), [types]);
}

/** 業務種別名 → 絵文字 の対応。マスタに無い名称は既定絵文字を返す */
export function workTypeEmoji(types: WorkTypeMaster[], name?: string): string {
  if (!name) return '📋';
  return types.find(t => t.name === name)?.emoji
    ?? DEFAULT_WORK_TYPES.find(t => t.name === name)?.emoji
    ?? '📋';
}
